import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  MAPPINGARTEN,
  MAPPINGZIEL,
  UMGEBUNGEN,
  abgleichen as sendeAbgleich,
  endpunkteSetzen as sendeEndpunkte,
  mapping as holeMapping,
  mappingEntfernen as sendeMappingEntfernen,
  mappingSetzen as sendeMappingSetzen,
  nachziehen as sendeNachziehen,
  protokoll as holeProtokoll,
  protokollBereinigen as sendeBereinigen,
  protokollBereinigung as holeBereinigung,
  stand as holeStand,
  umgebungen as holeUmgebungen,
  umschalten as sendeUmschalten,
  wiederholen as sendeWiederholen,
  zuruecksetzen as sendeZuruecksetzen,
  type Bereinigungsvorschau,
  type Mappingart,
  type Mappingzeile,
  type Protokollfilter,
  type Protokollzeile,
  type Stand,
  type Standzeile,
  type Umgebung,
  type Umgebungskennung,
} from '../api/navision';
import { ApiFehler } from '../api/fehler';
import { alsTag } from '../datum';
import { markenklasse, type Markenart } from '../marken';
import { Dialog } from '../komponenten/Dialog';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { zaehlwort } from '../komponenten/Seitenkontext';
import { Meldung } from '../komponenten/Meldung';
import { alsEuro } from '../betrag';

/** Die vier Zustände aus FIB-09, in der Sprache der Maske. */
const ZUSTANDSNAME: Readonly<Record<string, string>> = {
  nicht_uebertragen: 'nicht übertragen',
  in_uebertragung: 'in Übertragung',
  uebertragen: 'übertragen',
  fehlerhaft: 'fehlerhaft',
};

/**
 * Welche Marke ein Zustand aus FIB-09 trägt. Fehlerhaft ist eine Störung
 * (rot), übertragen das Ziel; die beiden übrigen sind der laufende Betrieb.
 */
const ZUSTANDSMARKE: Readonly<Record<string, Markenart>> = {
  nicht_uebertragen: 'laufend',
  in_uebertragung: 'laufend',
  uebertragen: 'abschluss',
  fehlerhaft: 'sackgasse',
};

const BELEGARTNAME: Readonly<Record<string, string>> = {
  rechnung: 'Rechnungen',
  gutschrift: 'Gutschriften',
  debitor: 'Debitoren',
  artikel: 'Artikel',
  /* FIB-25: Erstattung und Verrechnung eines Guthabens. */
  erstattung: 'Erstattungen',
  verrechnung: 'Verrechnungen',
};

/**
 * Wie viele Protokollzeilen die Maske auf einmal zeigt.
 *
 * Fuenfzig: genug, um einen Vormittag zu ueberblicken, wenig genug, dass die
 * Seite nicht zur Bleiwueste wird. Wer weiter zurueck muss, filtert — die
 * Suche ist der bessere Weg als eine lange Liste.
 */
const SEITE = 50;

/**
 * Einen Filter setzen — oder ihn WEGNEHMEN.
 *
 * `exactOptionalPropertyTypes` unterscheidet „Feld fehlt" von „Feld ist
 * undefined", und das ist hier keine Schikane: Ein `suche: undefined` ginge
 * als Abfrageteil `suche=undefined` hinaus und suchte nach dem Wort. Ein
 * leerer Wert muss die Eigenschaft ENTFERNEN.
 */
function mitFilter<S extends keyof Protokollfilter>(
  bisher: Protokollfilter,
  name: S,
  wert: Protokollfilter[S] | '' | false | undefined,
): Protokollfilter {
  const { [name]: _weg, ...rest } = bisher;
  if (wert === undefined || wert === '' || wert === false) return rest;
  return { ...rest, [name]: wert };
}

/**
 * Ein Zeitstempel für die Protokollzeile.
 *
 * Ortszeit und in einem Stück: Wer nach einer Störung nachsieht, vergleicht
 * die Zeile mit der Uhr an der Wand und nicht mit UTC. `toLocaleString` ist
 * derselbe Weg wie in `MeinKonto` und im Buchungsvorgang.
 */
function alsZeitpunkt(wert: string | null): string {
  return wert === null ? '—' : new Date(wert).toLocaleString('de-DE');
}

/**
 * Nach welchem Eingriff das Pop-up gerade fragt (CRM-104).
 *
 * Höchstens eine Rückfrage ist offen; die Zeile, um die es geht, reist mit —
 * so nennt das Pop-up genau die Zuordnung, deren Knopf gedrückt wurde.
 */
type Nachfrage =
  | { readonly art: 'umschalten'; readonly ziel: Umgebungskennung }
  | { readonly art: 'zuordnung'; readonly zeile: Mappingzeile }
  | { readonly art: 'zuruecksetzen' };

/** Die Zahl als Zahl — die Warteschlange liefert sie als Text (bigint). */
function summe(
  zeilen: readonly Standzeile[],
  felder: readonly ('offen' | 'laufend' | 'uebertragen' | 'fehlerhaft')[],
): number {
  return zeilen.reduce((s, z) => s + felder.reduce((t, f) => t + Number(z[f]), 0), 0);
}

/** Der heutige Tag als ISO-Datum, ohne Zeitzonensprung. */
function heute(): string {
  const jetzt = new Date();
  return new Date(jetzt.getTime() - jetzt.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/**
 * Alte Einträge aus dem Protokoll entfernen (LOG-11) — die Rückfrage.
 *
 * Sie nennt die ANZAHL, bevor etwas geschieht: „Entfernen" ohne Zahl ist eine
 * Handlung ins Ungewisse. Die Grenze ist mit der eingestellten Frist
 * vorbelegt; jede Änderung fragt die Zahl neu ab. Unter der Untergrenze
 * bleibt der Knopf gesperrt — der Dienst weist es ohnehin ab.
 */
function ProtokollEntfernen({
  entfernen,
  beiSchliessen,
}: {
  readonly entfernen: (tage: number) => Promise<void>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [eingabe, setzeEingabe] = useState('');
  const [vorschau, setzeVorschau] = useState<Bereinigungsvorschau | undefined>(undefined);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  // Zuerst mit der eingestellten Frist; sie belegt das Feld vor.
  useEffect(() => {
    void holeBereinigung()
      .then((v) => {
        setzeVorschau(v);
        setzeEingabe(String(v.tage));
      })
      .catch((ursache: unknown) => {
        if (ursache instanceof ApiFehler) setzeFehler(ursache);
      });
  }, []);

  const untergrenze = vorschau?.untergrenze ?? 7;
  const tage = /^\d{1,4}$/.test(eingabe) ? Number(eingabe) : undefined;
  const zulaessig = tage !== undefined && tage >= untergrenze;

  useEffect(() => {
    if (!zulaessig || tage === vorschau?.tage) return;
    void holeBereinigung(tage)
      .then(setzeVorschau)
      .catch((ursache: unknown) => {
        if (ursache instanceof ApiFehler) setzeFehler(ursache);
      });
  }, [tage, zulaessig, vorschau?.tage]);

  const aktuell = vorschau !== undefined && vorschau.tage === tage;

  return (
    <Rueckfrage
      frage="Alte Einträge entfernen?"
      unterzeile="Navision-Protokoll"
      tat="Entfernen"
      gesperrt={!zulaessig || !aktuell || vorschau.anzahl === 0}
      beiSchliessen={beiSchliessen}
      beiBestaetigen={async () => {
        if (tage !== undefined) await entfernen(tage);
      }}
    >
      {fehler !== undefined && <Meldung fehler={fehler} />}
      <div className="feld">
        <label className="feld__beschriftung" htmlFor="nav-protokoll-tage">
          Älter als (Tage)
        </label>
        <input
          id="nav-protokoll-tage"
          type="number"
          inputMode="numeric"
          min={untergrenze}
          value={eingabe}
          onChange={(e) => setzeEingabe(e.target.value)}
        />
        <p className="feld__hilfe">
          Eingestellte Frist: {vorschau === undefined ? '…' : `${String(vorschau.frist)} Tage`},
          mindestens {String(untergrenze)} Tage.
        </p>
      </div>
      <p role="status">
        {!zulaessig
          ? `Bitte mindestens ${String(untergrenze)} Tage angeben.`
          : aktuell
            ? `${zaehlwort(vorschau.anzahl, 'abgeschlossener Eintrag', 'abgeschlossene Einträge') ?? ''} älter als ${String(tage)} Tage ${vorschau.anzahl === 1 ? 'wird' : 'werden'} aus dem Protokoll entfernt.`
            : 'Anzahl wird ermittelt …'}
      </p>
      <p className="feld__hilfe">
        Offene und fehlerhafte Übertragungen bleiben stehen. Übertragungsstand und
        Navision-Nummer am Beleg bleiben erhalten; entfernt werden die übermittelten Daten.
      </p>
    </Rueckfrage>
  );
}

/**
 * Die Navision-Anbindung (Kap. 9, SYS-10).
 *
 * DREI DINGE, DIE DIESE MASKE SICHTBAR MACHT, WEIL SIE SONST NIEMAND SIEHT:
 *
 * 1. **In welches Navision gebucht wird.** FIB-18 lässt zwischen produktiv
 *    und Sandbox umschalten; von außen ist der Unterschied nur an der
 *    Belegnummer zu erkennen, und dann erst hinterher. Die Umgebung steht
 *    deshalb ganz oben und nicht in einer Fußzeile.
 *
 * 2. **Welcher Adapter antwortet.** Steht dort „Attrappe", war überhaupt kein
 *    Navision beteiligt — die Zahlen darunter sehen trotzdem aus wie
 *    gebuchte. Das ist die gefährlichste Verwechslung der ganzen Anbindung
 *    und deshalb eine eigene, laute Zeile.
 *
 * 3. **Was liegengeblieben ist.** Ein Beleg im Dead-Letter (FIB-10) wartet
 *    auf einen Menschen. Ohne diese Liste fiele er erst beim Monatsabschluss
 *    auf.
 *
 * WAS DIESE MASKE NICHT KANN: Kennwörter setzen. Sie stehen in der
 * Betriebskonfiguration; hier steht nur der **Name** des Zugangs, aus dem der
 * Adapter seine Umgebungsvariablen bildet. Ein Kennwort, das hier eingegeben
 * würde, müsste irgendwo liegen — und der einzige Ort wäre die Datenbank,
 * also jede Sicherung und jeder Prüfungsauszug (BP-05). Dieselbe Überlegung
 * wie bei den Postfächern.
 */
export function Navision({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [stand, setzeStand] = useState<Stand | undefined>(undefined);
  const [umgebungen, setzeUmgebungen] = useState<readonly Umgebung[]>([]);
  const [gewaehlt, setzeGewaehlt] = useState<Umgebungskennung>('sandbox');
  const [zuordnungen, setzeZuordnungen] = useState<readonly Mappingzeile[]>([]);
  const [neueArt, setzeNeueArt] = useState<Mappingart>('kundengruppe');
  const [neueQuelle, setzeNeueQuelle] = useState('');
  const [neuesZiel, setzeNeuesZiel] = useState('');
  const [tag, setzeTag] = useState(heute());
  /* FIB-11: die letzten Laeufe, mit ihren Filtern. */
  const [protokoll, setzeProtokoll] = useState<readonly Protokollzeile[]>([]);
  const [protokollGesamt, setzeProtokollGesamt] = useState(0);
  const [protokollFilter, setzeProtokollFilter] = useState<Protokollfilter>({});
  const [suche, setzeSuche] = useState('');
  /* LOG-11: die Rückfrage zum Entfernen alter Einträge. */
  const [entfernenOffen, setzeEntfernenOffen] = useState(false);
  /* CRM-104: die übrigen Eingriffe fragen ebenfalls im Pop-up nach. */
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | undefined>(undefined);
  /** Welche Umgebungszeile gerade bearbeitet wird, und ihr Entwurf. */
  const [bearbeitet, setzeBearbeitet] = useState<Umgebung | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (): Promise<void> => {
    const [s, u] = await Promise.all([holeStand(), holeUmgebungen()]);
    setzeStand(s);
    setzeUmgebungen(u);
  }, []);

  useEffect(() => {
    void laden().catch((ursache: unknown) => {
      if (ursache instanceof ApiFehler) setzeFehler(ursache);
    });
  }, [laden]);

  /*
   * DAS PROTOKOLL LAEDT FUER SICH.
   *
   * Es haengt an seinen Filtern und nicht am uebrigen Stand: Wer einen Filter
   * umstellt, soll nicht die Umgebungen und Zuordnungen mitladen — und wer
   * eine Zuordnung pflegt, nicht das halbe Protokoll.
   */
  const ladeProtokoll = useCallback(async (): Promise<void> => {
    const antwort = await holeProtokoll({ ...protokollFilter, grenze: SEITE });
    setzeProtokoll(antwort.zeilen);
    setzeProtokollGesamt(antwort.gesamt);
  }, [protokollFilter]);

  useEffect(() => {
    void ladeProtokoll().catch((ursache: unknown) => {
      if (ursache instanceof ApiFehler) setzeFehler(ursache);
    });
  }, [ladeProtokoll]);

  useEffect(() => {
    void holeMapping(gewaehlt)
      .then(setzeZuordnungen)
      .catch((ursache: unknown) => {
        if (ursache instanceof ApiFehler) setzeFehler(ursache);
      });
  }, [gewaehlt]);

  /**
   * Eine Handlung ausführen und ihre Rückmeldung anzeigen.
   *
   * `was` darf einen Text ZURÜCKGEBEN; dann gilt der statt `gelungen`. Das
   * braucht, wer erst nach der Antwort weiß, was er melden will — „6 gesehen,
   * 2 eingereiht" steht vorher nicht fest. Ein `setzeRueckmeldung` INNERHALB
   * von `was` wäre nutzlos: Es würde eine Zeile später überschrieben.
   */
  async function fuehreAus(
    was: () => Promise<string | void>,
    gelungen: string,
  ): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const eigene = await was();
      setzeRueckmeldung(typeof eigene === 'string' ? eigene : gelungen);
    } catch (ursache) {
      if (ursache instanceof ApiFehler) setzeFehler(ursache);
      else throw ursache;
    } finally {
      setzeLaeuft(false);
    }
  }

  /**
   * Ein Eingriff aus der Rückfrage (CRM-104).
   *
   * Anders als `fuehreAus` fängt er keinen Fehler: Der bleibt im Pop-up, und
   * es bleibt offen. Erst der Erfolg schließt es. Das Nachladen danach geht
   * an der Rückfrage vorbei — scheitert es, ist der Eingriff trotzdem
   * geschehen, und ein Fehler im Pop-up lüde zum zweiten Klick ein.
   */
  async function bestaetigt(
    aufruf: () => Promise<string>,
    nachladen: () => Promise<void>,
  ): Promise<void> {
    const text = await aufruf();
    setzeNachfrage(undefined);
    setzeFehler(undefined);
    setzeRueckmeldung(text);
    void nachladen().catch((ursache: unknown) => {
      if (ursache instanceof ApiFehler) setzeFehler(ursache);
    });
  }

  const aktiv = stand?.umgebung;
  const bezeichnungVon = (u: Umgebungskennung): string =>
    umgebungen.find((x) => x.kennung === u)?.bezeichnung ?? u;
  /* Die Warteschlange der geschalteten Umgebung — die Zahlen der Rückfragen. */
  const eigeneZeilen = (stand?.warteschlange ?? []).filter((z) => z.umgebung === aktiv?.kennung);

  const aufgegeben = stand?.aufgegeben ?? [];

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {/*
        SEITENTYP EINSTELLUNGEN (ÄA-41): je Thema eine Karte. Umgebung und
        Zuordnungen stehen nebeneinander, die Tabellen (Endpunkte,
        Übertragungen, Protokoll, Abgleich) über die ganze Breite.
      */}
      <Einstellungsseite>

        {/* ------------------------------------------------------------------
            1. Wohin gebucht wird (FIB-18) — die lauteste Aussage der Maske.
           ------------------------------------------------------------------ */}
        <Thema
          titel="Geschaltete Umgebung"
          zusatz={
            aktiv === undefined ? undefined : stand?.erreichbar === true ? (
              <span className={markenklasse('abschluss')}>erreichbar</span>
            ) : (
              /* FIB-18: Eine Umgebung, die nicht antwortet, ist eine Störung. */
              <span className={markenklasse('sackgasse')}>nicht erreichbar</span>
            )
          }
        >
          {aktiv === undefined ? (
            <p role="alert">
              Es ist keine Umgebung geschaltet. Bis eine gewählt ist, wird nichts übertragen —
              eingereihte Belege bleiben liegen und gehen nicht verloren.
            </p>
          ) : (
            <div className="hinweiskarte">
              <p>
                <strong>{aktiv.bezeichnung}</strong>
                {aktiv.kennung === 'sandbox' && (
                  <>
                    {' '}
                    <span className={markenklasse('aufmerksamkeit')}>Sandbox — keine echte Buchung</span>
                  </>
                )}
                {aktiv.adapter === 'attrappe' && (
                  /*
                    Die wichtigste Marke dieser Maske. Eine Übertragung gegen die
                    Attrappe erscheint in der Warteschlange unten als
                    „übertragen" — ohne diesen Hinweis läse jemand daraus, die
                    Buchhaltung sei angebunden.
                  */
                  <>
                    {' '}
                    <span className={markenklasse('aufmerksamkeit')}>
                      Attrappe — kein Navision beteiligt
                    </span>
                  </>
                )}
              </p>
              <p className="feld__hilfe">
                {aktiv.zugangsschluessel === null
                  ? 'Kein Zugangsname hinterlegt — die Umgebung gilt als nicht eingerichtet.'
                  : `Zugangsdaten unter NAV_${aktiv.zugangsschluessel}_BENUTZER, _KENNWORT, _DOMAENE.`}{' '}
                Die Werte stehen in der Betriebskonfiguration und nie in der Datenbank.
              </p>
            </div>
          )}

          {darfPflegen && (
            <div className="feld">
              <span className="feld__beschriftung">Umschalten auf</span>
              <div className="liste__leiste">
                {UMGEBUNGEN.map((u) => (
                  <button
                    key={u}
                    type="button"
                    className="knopf knopf--klein"
                    disabled={laeuft || aktiv?.kennung === u}
                    onClick={() => setzeNachfrage({ art: 'umschalten', ziel: u })}
                  >
                    {bezeichnungVon(u)} …
                  </button>
                ))}
              </div>
              <p className="feld__hilfe">
                Die Umschaltung wirkt sofort und ohne Neustart. Bereits eingereihte
                Belege bleiben bei <em>ihrer</em> Umgebung — ein Beleg, der für die Sandbox
                eingereiht wurde, geht nicht nachträglich ins produktive Navision.
              </p>
            </div>
          )}

          {/*
            FIB-18: Die Umschaltung wirkt sofort. Was danach eingereiht wird,
            geht in die neue Umgebung und kommt von dort nicht zurück — darum
            fragt sie nach, obwohl sie sich zurückschalten lässt.
          */}
          {darfPflegen && nachfrage?.art === 'umschalten' && (
            <Rueckfrage
              frage="Umgebung umschalten?"
              unterzeile={`${aktiv?.bezeichnung ?? 'keine Umgebung'} → ${bezeichnungVon(nachfrage.ziel)}`}
              tat="Umschalten"
              beiSchliessen={() => setzeNachfrage(undefined)}
              beiBestaetigen={() =>
                bestaetigt(
                  async () => {
                    setzeUmgebungen(await sendeUmschalten(nachfrage.ziel));
                    return `Es wird jetzt nach „${nachfrage.ziel}" gebucht.`;
                  },
                  laden,
                )
              }
            >
              <p>
                Ab sofort geht jede neue Übertragung nach{' '}
                <strong>{bezeichnungVon(nachfrage.ziel)}</strong>
                {nachfrage.ziel === 'produktiv' ? ' — in die echte Buchhaltung' : ' — keine echte Buchung'}.
                {aktiv !== undefined &&
                  ` ${zaehlwort(summe(eigeneZeilen, ['offen', 'laufend']), 'offene Übertragung bleibt', 'offene Übertragungen bleiben') ?? ''} bei ${aktiv.bezeichnung}.`}
              </p>
            </Rueckfrage>
          )}
        </Thema>

        {/* ------------------------------------------------------------------
            3. Die Zuordnungen (FIB-04)
           ------------------------------------------------------------------ */}
        <Thema titel="Zuordnungen" zusatz={zaehlwort(zuordnungen.length, 'Zeile', 'Zeilen')}>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="nav-umgebung">
              Umgebung
            </label>
            <select
              id="nav-umgebung"
              value={gewaehlt}
              onChange={(e) => setzeGewaehlt(e.target.value as Umgebungskennung)}
            >
              {UMGEBUNGEN.map((u) => (
                <option key={u} value={u}>
                  {umgebungen.find((x) => x.kennung === u)?.bezeichnung ?? u}
                </option>
              ))}
            </select>
            <p className="feld__hilfe">
              Jede Umgebung führt ihr <strong>eigenes</strong> Mapping. Dieselbe
              Kundengruppe zeigt in der Sandbox auf einen anderen Code als produktiv — ein
              gemeinsames Mapping hätte beim Umschalten stillschweigend falsch gebucht.
            </p>
          </div>

          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Bei uns</th>
                  <th scope="col">In Navision</th>
                  <th scope="col">Wert</th>
                  {darfPflegen && <th scope="col">&nbsp;</th>}
                </tr>
              </thead>
              <tbody>
                {zuordnungen.length === 0 && (
                  <tr>
                    <td colSpan={darfPflegen ? 4 : 3}>
                      Noch keine Zuordnung. Ohne sie scheitert jede Übertragung — und zwar
                      endgültig, denn eine fehlende Zeile verschwindet nicht durch Warten.
                    </td>
                  </tr>
                )}
                {zuordnungen.map((z) => (
                  <tr key={`${z.art}:${z.quelle}`}>
                    <td>
                      {z.art} <span className="kanalliste__name">· {z.quelle}</span>
                    </td>
                    <td>{MAPPINGZIEL[z.art]}</td>
                    <td>{z.ziel}</td>
                    {darfPflegen && (
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--eingriff knopf--klein"
                          disabled={laeuft}
                          onClick={() => setzeNachfrage({ art: 'zuordnung', zeile: z })}
                        >
                          Entfernen …
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {darfPflegen && (
            <div className="liste__leiste">
              <select
                aria-label="Art der Zuordnung"
                value={neueArt}
                onChange={(e) => setzeNeueArt(e.target.value as Mappingart)}
              >
                {MAPPINGARTEN.map((a) => (
                  <option key={a} value={a}>
                    {a} → {MAPPINGZIEL[a]}
                  </option>
                ))}
              </select>
              <input
                aria-label="Wert bei uns"
                placeholder="Wert bei uns"
                value={neueQuelle}
                onChange={(e) => setzeNeueQuelle(e.target.value)}
              />
              <input
                aria-label="Code in Navision"
                placeholder="Code in Navision"
                value={neuesZiel}
                onChange={(e) => setzeNeuesZiel(e.target.value)}
              />
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={laeuft || neueQuelle.trim() === '' || neuesZiel.trim() === ''}
                onClick={() =>
                  void fuehreAus(async () => {
                    setzeZuordnungen(
                      await sendeMappingSetzen({
                        umgebung: gewaehlt,
                        art: neueArt,
                        quelle: neueQuelle.trim(),
                        ziel: neuesZiel.trim(),
                        bemerkung: null,
                      }),
                    );
                    setzeNeueQuelle('');
                    setzeNeuesZiel('');
                  }, 'Die Zuordnung steht.')
                }
              >
                Übernehmen
              </button>
            </div>
          )}

          {/* FIB-04: Ohne die Zeile scheitert jede Übertragung, die sie braucht. */}
          {darfPflegen && nachfrage?.art === 'zuordnung' && (
            <Rueckfrage
              frage="Zuordnung entfernen?"
              unterzeile={bezeichnungVon(nachfrage.zeile.umgebung)}
              tat="Entfernen"
              beiSchliessen={() => setzeNachfrage(undefined)}
              beiBestaetigen={() =>
                bestaetigt(
                  async () => {
                    setzeZuordnungen(
                      await sendeMappingEntfernen(
                        nachfrage.zeile.umgebung,
                        nachfrage.zeile.art,
                        nachfrage.zeile.quelle,
                      ),
                    );
                    return 'Die Zuordnung wurde entfernt.';
                  },
                  () => Promise.resolve(),
                )
              }
            >
              <p>
                {nachfrage.zeile.art} „{nachfrage.zeile.quelle}" → {MAPPINGZIEL[nachfrage.zeile.art]}{' '}
                „{nachfrage.zeile.ziel}" gilt dann nicht mehr. Jede Übertragung, die diese
                Zuordnung braucht, scheitert, bis wieder eine steht.
              </p>
            </Rueckfrage>
          )}
        </Thema>

        {/* ------------------------------------------------------------------
            2. Endpunkte je Umgebung (FIB-02)
           ------------------------------------------------------------------ */}
        <Thema titel="Endpunkte und Zugangsnamen" breit>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Umgebung</th>
                  <th scope="col">SOAP</th>
                  <th scope="col">OData</th>
                  <th scope="col">Zugangsname</th>
                  <th scope="col">Adapter</th>
                </tr>
              </thead>
              <tbody>
                {umgebungen.map((u) => (
                  <tr key={u.kennung}>
                    <td>
                      {u.bezeichnung}
                      {u.aktiv && (
                        <>
                          {' '}
                          <span className={markenklasse('laufend')}>geschaltet</span>
                        </>
                      )}
                    </td>
                    <td>{u.soapendpunkt ?? '—'}</td>
                    <td>{u.odataendpunkt ?? '—'}</td>
                    <td>{u.zugangsschluessel ?? '—'}</td>
                    <td>
                      {u.adapter === 'attrappe' ? 'Attrappe' : 'SOAP/OData'}
                      {darfPflegen && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laeuft}
                          onClick={() => setzeBearbeitet(u)}
                        >
                          Ändern
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="feld__hilfe">
            Endpunkte sind ohne neue Programmfassung änderbar. Kennwörter nimmt diese
            Maske nicht entgegen — hier steht nur, <em>wie</em> die Umgebungsvariablen heißen.
          </p>

          {bearbeitet !== undefined && (
            <Dialog
              titel={bearbeitet.bezeichnung}
              unterzeile="Endpunkte und Zugangsname"
              breite="schmal"
              beiSchliessen={() => setzeBearbeitet(undefined)}
            >
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="nav-soap">
                  SOAP-Endpunkt
                </label>
                <input
                  id="nav-soap"
                  value={bearbeitet.soapendpunkt ?? ''}
                  onChange={(e) =>
                    setzeBearbeitet({
                      ...bearbeitet,
                      soapendpunkt: e.target.value === '' ? null : e.target.value,
                    })
                  }
                />
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="nav-odata">
                  OData-Endpunkt
                </label>
                <input
                  id="nav-odata"
                  value={bearbeitet.odataendpunkt ?? ''}
                  onChange={(e) =>
                    setzeBearbeitet({
                      ...bearbeitet,
                      odataendpunkt: e.target.value === '' ? null : e.target.value,
                    })
                  }
                />
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="nav-schluessel">
                  Zugangsname
                </label>
                <input
                  id="nav-schluessel"
                  value={bearbeitet.zugangsschluessel ?? ''}
                  onChange={(e) =>
                    setzeBearbeitet({
                      ...bearbeitet,
                      zugangsschluessel: e.target.value === '' ? null : e.target.value.toUpperCase(),
                    })
                  }
                />
                {/*
                  Der NAME, nicht das Kennwort. Aus „NAV_PROD" bildet der Adapter
                  NAV_PROD_BENUTZER und so fort; die Werte stehen in der
                  Betriebskonfiguration (BP-05).
                */}
                <p className="feld__hilfe">
                  Daraus werden NAV_&lt;Name&gt;_BENUTZER, _KENNWORT und _DOMAENE gelesen. Das
                  Kennwort selbst wird hier nicht eingegeben.
                </p>
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="nav-adapter">
                  Adapter
                </label>
                <select
                  id="nav-adapter"
                  value={bearbeitet.adapter}
                  onChange={(e) =>
                    setzeBearbeitet({
                      ...bearbeitet,
                      adapter: e.target.value as 'attrappe' | 'soap',
                    })
                  }
                >
                  <option value="attrappe">Attrappe — kein Navision beteiligt</option>
                  <option value="soap">SOAP/OData</option>
                </select>
              </div>
              <div className="liste__leiste">
                <button
                  type="button"
                  className="knopf knopf--klein"
                  disabled={laeuft}
                  onClick={() => setzeBearbeitet(undefined)}
                >
                  Verwerfen
                </button>
                <button
                  type="button"
                  className="knopf knopf--klein"
                  disabled={laeuft}
                  onClick={() =>
                    void fuehreAus(async () => {
                      setzeUmgebungen(
                        await sendeEndpunkte(bearbeitet.kennung, {
                          soapEndpunkt: bearbeitet.soapendpunkt,
                          odataEndpunkt: bearbeitet.odataendpunkt,
                          zugangsschluessel: bearbeitet.zugangsschluessel,
                          adapter: bearbeitet.adapter,
                        }),
                      );
                      setzeBearbeitet(undefined);
                      await laden();
                    }, 'Die Umgebung wurde geändert.')
                  }
                >
                  Übernehmen
                </button>
              </div>
            </Dialog>
          )}
        </Thema>

        {/* ------------------------------------------------------------------
            4. Warteschlange und Dead-Letter (FIB-11, FIB-09, FIB-10)
           ------------------------------------------------------------------ */}
        <Thema
          titel="Übertragungen"
          zusatz={
            /* FIB-10: Was liegenbleibt, wartet auf einen Menschen — in Rot. */
            aufgegeben.length > 0 ? (
              <span className={markenklasse('sackgasse')}>{aufgegeben.length} liegengeblieben</span>
            ) : undefined
          }
          breit
        >
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Gegenstand</th>
                  <th scope="col">offen</th>
                  <th scope="col">läuft</th>
                  <th scope="col">übertragen</th>
                  <th scope="col">fehlerhaft</th>
                  <th scope="col">aufgegeben</th>
                  <th scope="col">zuletzt</th>
                </tr>
              </thead>
              <tbody>
                {(stand?.warteschlange ?? []).map((z) => (
                  <tr key={`${z.umgebung}:${z.belegart}`}>
                    <td>
                      {BELEGARTNAME[z.belegart] ?? z.belegart}
                      {z.umgebung !== aktiv?.kennung && (
                        <>
                          {' '}
                          <span className={markenklasse('stillgelegt')}>{z.umgebung}</span>
                        </>
                      )}
                    </td>
                    <td>{z.offen}</td>
                    <td>{z.laufend}</td>
                    <td>{z.uebertragen}</td>
                    <td>
                      {Number(z.fehlerhaft) > 0 ? (
                        <span className={markenklasse('sackgasse')}>{z.fehlerhaft}</span>
                      ) : (
                        z.fehlerhaft
                      )}
                    </td>
                    <td>
                      {Number(z.aufgegeben) > 0 ? (
                        <span className={markenklasse('sackgasse')}>{z.aufgegeben}</span>
                      ) : (
                        z.aufgegeben
                      )}
                    </td>
                    <td>{z.letzteuebertragung ?? '—'}</td>
                  </tr>
                ))}
                {(stand?.warteschlange ?? []).length === 0 && (
                  <tr>
                    <td colSpan={7}>Noch nichts eingereiht.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {aufgegeben.length > 0 && (
            <>
              <h3>Liegengeblieben</h3>
              <p className="feld__hilfe">
                Diese Belege warten auf einen Menschen. Beheben Sie die genannte
                Ursache — meist eine fehlende Zuordnung — und holen Sie den Beleg dann zurück.
              </p>
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle">
                  <thead>
                    <tr>
                      <th scope="col">Beleg</th>
                      <th scope="col">Zustand</th>
                      <th scope="col">Versuche</th>
                      <th scope="col">Meldung aus Navision</th>
                      {darfPflegen && <th scope="col">&nbsp;</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {aufgegeben.map((u) => (
                      <tr key={u.id}>
                        <td>
                          {u.belegschluessel}
                          <span className="kanalliste__name"> · {u.umgebung}</span>
                        </td>
                        <td>
                          <span className={markenklasse(ZUSTANDSMARKE[u.zustand] ?? 'laufend')}>
                            {ZUSTANDSNAME[u.zustand] ?? u.zustand}
                          </span>
                        </td>
                        <td>{u.versuche}</td>
                        <td>{u.letzterfehler ?? '—'}</td>
                        {darfPflegen && (
                          <td>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={laeuft}
                              onClick={() =>
                                void fuehreAus(async () => {
                                  await sendeWiederholen(u.id);
                                  await laden();
                                }, 'Der Beleg ist wieder eingereiht.')
                              }
                            >
                              Wiederholen
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Thema>

        {/* ------------------------------------------------------------------
            4a. Stammdaten nachziehen (FIB-03, FIB-05, Kap. 29)
           ------------------------------------------------------------------ */}
        {darfPflegen && (
          <Thema titel="Stammdaten nachziehen">
            {/* FIB-07 */}
            <p className="feld__hilfe">
              Kunden und Artikel werden übertragen, <em>wenn sie angelegt oder geändert
              werden</em>. Was schon da war, bevor die Anbindung stand,
              ist weder neu noch geändert — es wird hier eingereiht. Zweimal drücken
              schadet nicht: Was unverändert in der Warteschlange steht, kommt nicht
              noch einmal hinein.
            </p>
            <p className="feld__hilfe">
              <strong>Erneut senden</strong> ist für den Fall, dass die Gegenseite ihren
              Bestand verloren hat — ein zurückgespieltes Navision, ein neuer Mandant,
              ein neu gestarteter Sandbox-Behälter. Dann meldet die Schnittstelle
              „Debitor … ist hier nicht bekannt", obwohl er hier als übertragen geführt
              wird. Nur Stammdaten; Belege fasst dieser Weg nicht an.
            </p>

            <div className="wahlgruppe">
              {(['debitor', 'artikel'] as const).map((art) => (
                <span key={art} className="zeitraum">
                  <button
                    type="button"
                    className="knopf"
                    disabled={laeuft}
                    onClick={() =>
                      void fuehreAus(async () => {
                        const e = await sendeNachziehen(art);
                        await ladeProtokoll();
                        await laden();
                        return `${BELEGARTNAME[art] ?? art}: ${String(e.gesehen)} gesehen, ${String(e.eingereiht)} eingereiht.`;
                      }, '')
                    }
                  >
                    {BELEGARTNAME[art] ?? art} nachziehen
                  </button>
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    disabled={laeuft}
                    onClick={() =>
                      void fuehreAus(async () => {
                        const e = await sendeNachziehen(art, true);
                        await ladeProtokoll();
                        await laden();
                        return `${BELEGARTNAME[art] ?? art}: ${String(e.eingereiht)} erneut eingereiht.`;
                      }, '')
                    }
                  >
                    erneut senden
                  </button>
                </span>
              ))}
            </div>
          </Thema>
        )}

        {/* ------------------------------------------------------------------
            4a2. Eine Testumgebung zurücksetzen (FIB-18)
           ------------------------------------------------------------------ */}
        {darfPflegen && aktiv !== undefined && aktiv.kennung !== 'produktiv' && (
          <Thema titel="Testumgebung zurücksetzen">
            <p className="feld__hilfe">
              Verwirft die Übertragungsgeschichte der Umgebung{' '}
              <strong>{aktiv.bezeichnung}</strong> und reiht alles neu ein — Stammdaten
              und jeden festgeschriebenen Beleg. Nötig, wenn die Gegenseite einen
              <em> anderen Stand</em> hat als diese Aufzeichnung: Der Sandbox-Behälter
              hält seinen Bestand im Arbeitsspeicher, und nach einem Neustart vergibt er
              auch Belegnummern erneut, die hier schon vergeben sind.
            </p>
            {/* BP-15 */}
            <p className="feld__hilfe">
              {/*
                Der eingreifendste Vorgang dieser Maske. Er steht deshalb allein,
                trägt die Warnfarbe und nennt vorher, was er tut — nicht erst
                hinterher in einer Rückmeldung.
              */}
              <strong>Die produktive Umgebung lässt sich so nicht zurücksetzen.</strong>{' '}
              Dort ist die Übertragungsgeschichte der Nachweis, welcher Beleg wann in die
              Buchhaltung ging; einzelne Zeilen lassen sich stattdessen
              wiederholen.
            </p>
            <button
              type="button"
              className="knopf knopf--eingriff"
              disabled={laeuft}
              onClick={() => setzeNachfrage({ art: 'zuruecksetzen' })}
            >
              {aktiv.bezeichnung} zurücksetzen …
            </button>
            {nachfrage?.art === 'zuruecksetzen' && (
              <Rueckfrage
                frage="Umgebung zurücksetzen?"
                unterzeile={aktiv.bezeichnung}
                tat="Zurücksetzen"
                beiSchliessen={() => setzeNachfrage(undefined)}
                beiBestaetigen={() =>
                  bestaetigt(
                    async () => {
                      const e = await sendeZuruecksetzen(aktiv.kennung);
                      return `${aktiv.bezeichnung}: ${String(e.verworfen)} Zeilen verworfen, ${String(e.stammdaten)} Stammdaten und ${String(e.belege)} Belege neu eingereiht.`;
                    },
                    async () => {
                      await ladeProtokoll();
                      await laden();
                    },
                  )
                }
              >
                {/* FIB-18: Die Zahlen stammen aus der Warteschlange darüber. */}
                <p>
                  Die Übertragungsgeschichte von <strong>{aktiv.bezeichnung}</strong> wird
                  verworfen —{' '}
                  {zaehlwort(
                    summe(eigeneZeilen, ['offen', 'laufend', 'uebertragen', 'fehlerhaft']),
                    'Zeile',
                    'Zeilen',
                  )}
                  , davon {summe(eigeneZeilen, ['uebertragen'])} übertragen. Stammdaten und jeder
                  festgeschriebene Beleg werden neu eingereiht; das lässt sich nicht zurücknehmen.
                </p>
              </Rueckfrage>
            )}
          </Thema>
        )}

        {/* ------------------------------------------------------------------
            4b. Das Protokoll — die letzten Läufe (FIB-11, FIB-09)
           ------------------------------------------------------------------ */}
        <Thema
          titel="Protokoll"
          zusatz={`${protokoll.length} von ${protokollGesamt} Läufen`}
          breit
        >
          {/*
            ZWEI LISTEN MIT DEMSELBEN KNOPF — UND DAS IST ABSICHT.
            Der Abschnitt „Liegengeblieben" darüber erscheint nur, wenn etwas
            hängt, und fordert zur Handlung auf. Dieser hier ist das Gedächtnis:
            Man sucht darin, auch wenn nichts hängt. Die eine ruft, die andere
            erinnert sich.
          */}
          <p className="feld__hilfe">
            Die letzten Läufe der Schnittstelle. Die Tabelle darüber zählt, diese
            hier sagt, <em>was</em> wann durchging — und woran es lag, wenn nicht. Gesucht
            wird über Beleg&shy;nummer, Kunden&shy;nummer, Artikel&shy;nummer und die
            Navision-Nummer.
          </p>

          {/* LOG-11: Abgeschlossene Einträge laufen nach der Frist von selbst ab; hier sofort. */}
          {darfPflegen && (
            <div className="liste__leiste">
              <button
                type="button"
                className="knopf knopf--eingriff knopf--klein"
                disabled={laeuft}
                onClick={() => setzeEntfernenOffen(true)}
              >
                Alte Einträge entfernen …
              </button>
            </div>
          )}
          {darfPflegen && entfernenOffen && (
            <ProtokollEntfernen
              beiSchliessen={() => setzeEntfernenOffen(false)}
              entfernen={async (tage) => {
                // Ein Fehler bleibt im Pop-up (Rueckfrage); erst der Erfolg schließt es.
                const e = await sendeBereinigen(tage);
                setzeEntfernenOffen(false);
                setzeRueckmeldung(
                  `${zaehlwort(e.anzahl, 'Eintrag', 'Einträge') ?? ''} älter als ${String(e.tage)} Tage aus dem Protokoll entfernt.`,
                );
                await ladeProtokoll();
              }}
            />
          )}

          <div className="wahlgruppe">
            <label className="feld">
              <span className="feld__beschriftung">Suche</span>
              <input
                type="search"
                value={suche}
                placeholder="RE-000001, KD-000002, VR000001 …"
                onChange={(e) => setzeSuche(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setzeProtokollFilter((f) => mitFilter(f, 'suche', suche.trim()));
                  }
                }}
                onBlur={() =>
                  setzeProtokollFilter((f) => mitFilter(f, 'suche', suche.trim()))
                }
              />
            </label>

            <label className="feld">
              <span className="feld__beschriftung">Gegenstand</span>
              <select
                value={protokollFilter.belegart ?? ''}
                onChange={(e) =>
                  setzeProtokollFilter((f) =>
                    mitFilter(f, 'belegart', e.target.value as Protokollzeile['belegart'] | ''),
                  )
                }
              >
                <option value="">Alle Belegarten</option>
                {Object.entries(BELEGARTNAME).map(([wert, name]) => (
                  <option key={wert} value={wert}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <label className="feld">
              <span className="feld__beschriftung">Zustand</span>
              <select
                value={protokollFilter.zustand ?? ''}
                onChange={(e) =>
                  setzeProtokollFilter((f) =>
                    mitFilter(f, 'zustand', e.target.value as Protokollzeile['zustand'] | ''),
                  )
                }
              >
                <option value="">Alle Zustände</option>
                {Object.entries(ZUSTANDSNAME).map(([wert, name]) => (
                  <option key={wert} value={wert}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <label className="feld">
              <span className="feld__beschriftung">&nbsp;</span>
              <label className="liste__schalter">
                <input
                  type="checkbox"
                  checked={protokollFilter.nurAufgegebene ?? false}
                  onChange={(e) =>
                    setzeProtokollFilter((f) =>
                      mitFilter(f, 'nurAufgegebene', e.target.checked),
                    )
                  }
                />
                <span>nur liegengebliebene</span>
              </label>
            </label>
          </div>

          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Zuletzt</th>
                  <th scope="col">Gegenstand</th>
                  <th scope="col">Beleg</th>
                  <th scope="col">Zustand</th>
                  <th scope="col">Versuche</th>
                  <th scope="col">Navision</th>
                  <th scope="col">Meldung</th>
                  {darfPflegen && <th scope="col">&nbsp;</th>}
                </tr>
              </thead>
              <tbody>
                {protokoll.map((z) => (
                  <tr key={z.id}>
                    <td className="liste__einzeilig">{alsZeitpunkt(z.zuletzt)}</td>
                    <td>
                      {BELEGARTNAME[z.belegart] ?? z.belegart}
                      {z.umgebung !== aktiv?.kennung && (
                        <>
                          {' '}
                          <span className={markenklasse('stillgelegt')}>{z.umgebung}</span>
                        </>
                      )}
                    </td>
                    <td>
                      {/*
                        Der aufgelöste Beleg. Fehlt er — ein Kunde, den jemand
                        gelöscht hat —, steht der Belegschlüssel da: Er ist immer
                        vorhanden, und eine leere Zelle beantwortet nichts.
                      */}
                      {z.kennzeichen ?? z.belegschluessel}
                      {z.bezeichnung !== null && (
                        <span className="kanalliste__name"> · {z.bezeichnung}</span>
                      )}
                    </td>
                    <td>
                      <span className={markenklasse(ZUSTANDSMARKE[z.zustand] ?? 'laufend')}>
                        {ZUSTANDSNAME[z.zustand] ?? z.zustand}
                      </span>
                      {z.aufgegebenam !== null && (
                        <>
                          {' '}
                          <span className={markenklasse('sackgasse')}>liegengeblieben</span>
                        </>
                      )}
                    </td>
                    <td>{z.versuche}</td>
                    <td>{z.navbelegnummer ?? '—'}</td>
                    <td>{z.letzterfehler ?? '—'}</td>
                    {darfPflegen && (
                      <td>
                        {z.zustand === 'fehlerhaft' && (
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={laeuft}
                            onClick={() =>
                              void fuehreAus(async () => {
                                await sendeWiederholen(z.id);
                                await laden();
                                await ladeProtokoll();
                              }, 'Der Beleg ist wieder eingereiht.')
                            }
                          >
                            Wiederholen
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {protokoll.length === 0 && (
                  <tr>
                    <td colSpan={darfPflegen ? 8 : 7}>Zu diesen Merkmalen steht nichts an.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {protokollGesamt > protokoll.length && (
            <p className="feld__hilfe">
              {protokoll.length} von {protokollGesamt} Zeilen. Schränken Sie die Suche ein,
              um die übrigen zu sehen.
            </p>
          )}
        </Thema>

        {/* ------------------------------------------------------------------
            5. Vollständigkeitsabgleich (FIB-12)
           ------------------------------------------------------------------ */}
        <Thema titel="Vollständigkeitsabgleich" breit>
          <p className="feld__hilfe">
            Anzahl und Summe der übertragenen Rechnungen, hier und dort. Eine leere Spalte auf
            der Navision-Seite heißt <strong>keine Antwort</strong> — das ist etwas anderes als
            null Belege, und nur deshalb lässt sich ein misslungener Abgleich von einem
            leeren Tag unterscheiden.
          </p>

          {darfPflegen && (
            <div className="liste__leiste">
              <input
                type="date"
                aria-label="Tag des Abgleichs"
                value={tag}
                onChange={(e) => setzeTag(e.target.value)}
              />
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={laeuft}
                onClick={() =>
                  void fuehreAus(async () => {
                    await sendeAbgleich(tag);
                    await laden();
                  }, `Der Abgleich für ${tag} ist gelaufen.`)
                }
              >
                Jetzt abgleichen
              </button>
            </div>
          )}

          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Tag</th>
                  <th scope="col">Umgebung</th>
                  <th scope="col">CRM</th>
                  <th scope="col">Navision</th>
                  <th scope="col">Befund</th>
                </tr>
              </thead>
              <tbody>
                {(stand?.abgleiche ?? []).map((a) => (
                  <tr key={`${a.tag}:${a.umgebung}`}>
                    <td className="liste__einzeilig">{alsTag(a.tag)}</td>
                    <td>{a.umgebung}</td>
                    <td>
                      {a.crmanzahl} · {alsEuro(a.crmsumme)}
                    </td>
                    <td>
                      {a.navanzahl === null ? 'keine Antwort' : `${a.navanzahl} · ${alsEuro(a.navsumme)}`}
                    </td>
                    <td>
                      {a.abweichung ? (
                        <span className={markenklasse('sackgasse')}>Abweichung</span>
                      ) : (
                        <span className={markenklasse('abschluss')}>stimmt überein</span>
                      )}
                    </td>
                  </tr>
                ))}
                {(stand?.abgleiche ?? []).length === 0 && (
                  <tr>
                    <td colSpan={5}>Noch kein Abgleich gelaufen.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Thema>
      </Einstellungsseite>
    </>
  );
}
